import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllEventsByVenue, deleteVenueEvent } from "../../api";
import EventNotFound from "../../components/common/EventCard/EventNotFound";
import { EventType } from "../../types/Events";
import { images } from "../../assets/images";
import { useNavigate } from "react-router-dom";

import PulseLoader from "react-spinners/PulseLoader";

import Select from "react-select";
import VenueEventListCard from "../../components/common/EventCard/VenueEventListCard";

type EventsProps = {};

/**
 * Venue events list — same data/filters as before.
 * Layout uses theme filter rail so search + status + tabs sit on one even row
 * instead of the old Bootstrap wrap that looked uneven.
 */
const VenueEvents = (props: EventsProps) => {
  const navigate = useNavigate();
  const [eventList, setEventList] = useState<EventType[]>([]);
  const [onlineEventList, setOnlineEventList] = useState<EventType[]>([]);
  const [offlineEventList, setOfflineEventList] = useState<EventType[]>([]);
  const [currentTab, setCurrentTab] = useState("all");
  const [search, setSearch] = useState("");
  const [eventStatus, setEventStatus] = useState<"all" | "draft" | "active">("all");
  const [loading, setLoading] = useState<boolean>(true);

  const handleDeleteVenueEvent = async (eventId: number) => {
    try {
      await deleteVenueEvent(eventId);
      toast.success("Event deleted successfully");
      const updatedList = eventList.filter((e) => Number(e.eventId) !== eventId);
      setEventList(updatedList);
      setOnlineEventList(updatedList.filter((e) => e.eventType === "online"));
      setOfflineEventList(updatedList.filter((e) => e.eventType === "offline"));
    } catch (error) {
      console.error("Failed to delete event:", error);
      toast.error("Failed to delete event");
    }
  };

  const handleEditWrapper = (event: any) => {
    navigate("/edit-venue-event", {
      state: { event },
    });
  };

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "draft", label: "Draft" },
    { value: "active", label: "Active" },
  ];

  // Dark glass select — matches theme tokens (not Bootstrap blue)
  const customSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: "40px",
      height: "40px",
      background: "rgba(255, 255, 255, 0.05)",
      borderColor: state.isFocused ? "rgba(238, 63, 124, 0.45)" : "rgba(255, 255, 255, 0.08)",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(238, 63, 124, 0.15)" : "none",
      borderRadius: "12px",
      fontSize: "13px",
      width: "100%",
      minWidth: "140px",
      color: "#fff",
      cursor: "pointer",
      "&:hover": {
        borderColor: "rgba(238, 63, 124, 0.35)",
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "#fff",
      fontSize: "13px",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "rgba(255, 255, 255, 0.4)",
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: "0 10px",
    }),
    input: (provided: any) => ({
      ...provided,
      margin: "0px",
      color: "#fff",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: "40px",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      padding: "8px",
      color: "rgba(255, 255, 255, 0.45)",
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      background: "#16161c",
      border: "1px solid rgba(255, 255, 255, 0.08)",
      borderRadius: "12px",
      overflow: "hidden",
    }),
    // Portal menu must stay above page chrome (overflow-x:hidden clips in-place menus).
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "rgba(238, 63, 124, 0.85)"
        : state.isFocused
          ? "rgba(255, 255, 255, 0.06)"
          : "transparent",
      color: "#fff",
      fontSize: "13px",
      padding: "10px 12px",
      cursor: "pointer",
    }),
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const currentVenueId = localStorage.getItem("organizerId");

        if (!currentVenueId) {
          toast.error("Venue session missing. Please sign in again.");
          setLoading(false);
          return;
        }

        const response = await getAllEventsByVenue(currentVenueId);
        if (response) {
          const mappedEvents = response.map((event: any) => ({
            ...event,
            eventId: Number(event.event_id),
            eventName: event.event_name,
            eventDate: event.event_date,
            eventTime: event.event_time,
            shortDescription: event.event_description,
            eventDescription: event.event_description,
            eventStatus: event.event_status,
            bannerImgDesktop: event.event_image,
            thumbImg: event.event_image,
            eventType: event.event_type || (event.event_location ? "offline" : "online"),
            eventLocation: event.event_location,
            categoryId: Number(event.event_category),
          }));

          const now = new Date();
          const futureEvents = mappedEvents.filter(
            (event: EventType) => new Date(event.eventDate) >= now
          );
          const pastEvents = mappedEvents.filter(
            (event: EventType) => new Date(event.eventDate) < now
          );
          const sortedFuture = [...futureEvents].sort(
            (a: any, b: any) =>
              new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()
          );
          const sortedPast = [...pastEvents].sort(
            (a: any, b: any) =>
              new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime()
          );
          const combinedSortedEvents = [...sortedFuture, ...sortedPast];
          setEventList(combinedSortedEvents);
          const onlineEvents = combinedSortedEvents.filter(
            (event: EventType) => event.eventType === "online"
          );
          const offlineEvents = combinedSortedEvents.filter(
            (event: EventType) => event.eventType === "offline"
          );
          setOnlineEventList(onlineEvents);
          setOfflineEventList(offlineEvents);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        toast.error("Could not load venue events. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const handleSearch = (e: any) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
  };
  const filterEventsBySearch = (events: EventType[]) => {
    if (search.length === 0) {
      return events;
    }
    return events.filter(
      (event: EventType) =>
        event.eventName.toLowerCase().includes(search.toLowerCase()) ||
        (event.shortDescription &&
          event.shortDescription
            .toLowerCase()
            .includes(search.toLowerCase())) ||
        (event.eventStatus &&
          event.eventStatus.toLowerCase().includes(search.toLowerCase()))
    );
  };

  const formatEventDateTime = (dateStr: string, timeStr: string) => {
    if (!dateStr) return "Invalid Date";
    const dateTimeStr = timeStr ? `${dateStr}T${timeStr}` : dateStr;
    const date = new Date(dateTimeStr);
    if (isNaN(date.getTime())) return "Invalid Date";
    return `${date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "2-digit",
      year: "numeric",
    })}${timeStr ? ` ${date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}` : ""}`;
  };

  const filterEventsByStatus = (events: EventType[]) => {
    if (eventStatus === "all") {
      return events;
    }
    return events.filter((event: EventType) => {
      if (eventStatus === "draft") {
        return (
          event.eventStatus === "draft" ||
          event.status === "draft" ||
          event.eventStatus === "Draft"
        );
      } else if (eventStatus === "active") {
        return (
          event.eventStatus === "active" || event.eventStatus === "published"
        );
      }
      return true;
    });
  };

  const getFilteredEventCount = (tabType: string) => {
    let eventsToFilter: EventType[] = [];

    if (tabType === "online") {
      eventsToFilter = onlineEventList;
    } else if (tabType === "offline") {
      eventsToFilter = offlineEventList;
    } else {
      eventsToFilter = eventList;
    }

    const statusFiltered = filterEventsByStatus(eventsToFilter);
    const searchFiltered = filterEventsBySearch(statusFiltered);

    return searchFiltered.length;
  };

  const renderCount = () => {
    if (currentTab === "online") {
      return getFilteredEventCount("online");
    } else if (currentTab === "offline") {
      return getFilteredEventCount("offline");
    } else {
      return getFilteredEventCount("all");
    }
  };

  const getVisibleEvents = () => {
    let eventsToRender: EventType[] = [];
    if (currentTab === "online") {
      eventsToRender = filterEventsByStatus(onlineEventList);
    } else if (currentTab === "offline") {
      eventsToRender = filterEventsByStatus(offlineEventList);
    } else {
      eventsToRender = filterEventsByStatus(eventList);
    }
    return filterEventsBySearch(eventsToRender);
  };

  const renderEventTable = () => {
    return getVisibleEvents().map((event: EventType) => (
      <VenueEventListCard
        key={event.eventId}
        eventName={event.eventName}
        eventShortDescription={event.shortDescription || ""}
        bannerImg={event.bannerImgDesktop !== ""
          ? event.bannerImgDesktop
          : event.thumbImg !== ""
            ? event.thumbImg
            : images.noEvent}
        eventStatus={(() => {
          if (event.eventStatus === "draft") {
            return "Draft";
          } else if (event.eventStatus === "active") {
            return "Live";
          } else {
            return event.eventStatus || "Draft";
          }
        })()}
        eventDate={formatEventDateTime(event.eventDate, event.eventTime)}
        totalTicket={100}
        soldTicket={50}
        eventId={Number(event.eventId)}
        eventData={event}
        onDelete={handleDeleteVenueEvent}
        onEdit={handleEditWrapper}
        canEdit={(event as any).list_type === "event_list"}
        eventTime={undefined}
      />
    ));
  };

  return (
    <>
      <ToastContainer />

      <div className="ex-events-page">
        {/* Page header — one job: title + count */}
        <div className="ex-page-header">
          <span className="ex-page-eyebrow">Venue</span>
          <h1 className="ex-page-title">
            <i className="fa-solid fa-calendar-days me-2" aria-hidden="true"></i>
            Events
          </h1>
          <p className="ex-page-sub">{renderCount()} events</p>
        </div>

        {/* Single even filter rail: search | status | type tabs */}
        <div className="ex-filters-rail">
          <div className="ex-filter-search">
            <div className="relative-input position-relative">
              <input
                className="form-control h_40"
                type="text"
                placeholder="Search by event name, status"
                value={search}
                onChange={(e) => handleSearch(e)}
              />
              <i className="uil uil-search"></i>
            </div>
          </div>

          <div className="ex-filter-status">
            <Select
              options={statusOptions}
              value={statusOptions.find((option) => option.value === eventStatus)}
              onChange={(selectedOption: any) =>
                setEventStatus(selectedOption.value)
              }
              styles={customSelectStyles}
              isSearchable={false}
              placeholder="Select Status"
              className="react-select-container"
              classNamePrefix="react-select"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
            />
          </div>

          <div className="nav custom2-tabs btn-group ex-filter-tabs" role="tablist">
            <button
              className={`tab-link ${currentTab === "all" ? "active" : ""}`}
              onClick={() => setCurrentTab("all")}
              type="button"
              role="tab"
              aria-selected={currentTab === "all"}
            >
              All Event (
              <span className="total_event_counter">
                {getFilteredEventCount("all")}
              </span>
              )
            </button>
            <button
              className={`tab-link ${currentTab === "online" ? "active" : ""}`}
              onClick={() => setCurrentTab("online")}
              type="button"
              role="tab"
              aria-selected={currentTab === "online"}
            >
              Online Event (
              <span className="total_event_counter">
                {getFilteredEventCount("online")}
              </span>
              )
            </button>
            <button
              className={`tab-link ${currentTab === "offline" ? "active" : ""}`}
              onClick={() => setCurrentTab("offline")}
              type="button"
              role="tab"
              aria-selected={currentTab === "offline"}
            >
              Venue Event (
              <span className="total_event_counter">
                {getFilteredEventCount("offline")}
              </span>
              )
            </button>
          </div>
        </div>

        <div className="event-list">
          <div className="tab-content">
            {loading ? (
              <div className="d-flex justify-content-center my-5">
                <PulseLoader color="#EE3F7C" size={12} />
              </div>
            ) : getVisibleEvents().length <= 0 ? (
              <EventNotFound />
            ) : (
              renderEventTable()
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export default VenueEvents;
